interface ChargeTicketEntry {
  ticketType?: unknown;
  quantity?: number;
  price?: number;
  fee?: { totalAmount?: number } | null;
}

/**
 * A shared coupon stores one ticket type as several entries priced a cent apart. Merges them into
 * one entry per type: quantities and fee totals summed, the unit price the line average rounded to
 * the cent. A type with a single entry comes back as the same object.
 */
export function groupTicketsByType<T extends ChargeTicketEntry>(
  tickets: T[] | null | undefined,
): T[] {
  const byType = new Map<string, T[]>();
  const groups: T[][] = [];

  for (const ticket of tickets ?? []) {
    const key = ticket.ticketType == null ? null : String(ticket.ticketType);
    const group = key == null ? undefined : byType.get(key);

    if (group) {
      group.push(ticket);
    } else {
      const created = [ticket];
      groups.push(created);
      if (key != null) byType.set(key, created);
    }
  }

  return groups.map(entries => {
    if (entries.length === 1) return entries[0];

    const [first] = entries;
    const quantity = entries.reduce((sum, entry) => sum + (entry.quantity ?? 0), 0);
    const amount = entries.reduce(
      (sum, entry) => sum + (entry.price ?? 0) * (entry.quantity ?? 0),
      0,
    );

    return {
      ...first,
      quantity,
      price: quantity ? Math.round(amount / quantity) : first.price,
      ...(first.fee && {
        fee: {
          ...first.fee,
          totalAmount: entries.reduce((sum, entry) => sum + (entry.fee?.totalAmount ?? 0), 0),
        },
      }),
    };
  });
}
